<template>
    <div class="col-12 mx-auto">
        <page-title :title="$t('registers.employees.title')" :showTitle="false"></page-title>
        <div class="card">
            <div class="card-header bg-primary text-white">
                <div class="card-title h5 m-0">
                    {{ $t('registers.employees.title') }}
                </div>
            </div>

            <div class="card-header" v-if="showSettingsBtn">
                <button type="button" class="btn btn-primary btn-lg"
                        v-if="showNewButton || claims.nijEmployee.create"
                        @click="goToCreate">
                    {{$t('buttons.create.btnText')}}
                </button>

                <button type="button" class="btn btn-info btn-lg"
                        v-if="claims.nijEmployee.read"
                        @click="goToOrganogram">
                    <i class="fas fa-project-diagram"></i>
                    {{$t('registers.employees.organogram')}}
                </button>


                <button type="button" class="btn btn-primary btn-lg"
                        v-if="showExportTemplateButton"
                        @click="goToExportTemplate">
                    {{$t('buttons.exportTemplate.btnText')}}
                </button>
                <label>
                    <input type="checkbox" id="exportWithData" class="checkbox" v-if="showExportTemplateButton">
                    <span v-if="showExportTemplateButton">{{$t('checkboxes.exportWithData.cbText')}}</span>
                </label>

                <form v-if="claims.nijEmployee.create" enctype="multipart/form-data" method="post" id="importForm"
                      class="inlineForm">
                    <div class="inlineForm">
                        <button type="button" class="btn btn-primary btn-lg"
                                v-if="showImportFromExcelButton"
                                @click="selectImportFiles">
                            {{$t('buttons.importFromExcel.btnText')}}
                        </button>
                        <input type="file" id="importFile" class="selectFile" @change="goToImportFromExcel" />
                    </div>
                </form>


                <i class="fas fa-cogs pointer fa-2x float-right text-success"
                   v-if="showSettingsBtn"
                   @click.stop="changeGridSettings"
                   data-toggle="tooltip"
                   :title="settingsTooltipText"></i>

                <grid-user-settings ref="userSettings"
                                    :schemaModelFields="schemaModelFields"
                                    :componentName="componentName"
                                    :useLabelAsTitle="true"
                                    :showUserSettings="true"
                                    :gridOptions="userSettings"
                                    v-if="showUserSettingsForm"
                                    @saveGridSettings="saveGridSettings"
                                    @loadGridSettings="loadGridSettings"
                                    @deleteGridSettings="deleteGridSettings"
                                    @userSettingsChanged="userSettingsChanged">
                </grid-user-settings>

            </div>

            <div class="card-body">
                <div class="btn-toolbar justify-content-between mt-0 mb-2" role="toolbar">
                    <div class="form-inline col-lg-6 col-md-12">
                        <div class="col-3">
                            <button class="btn btn-secondary"
                                    @click.stop="refresh">
                                <i class="fas fa-filter"></i>
                                {{ $t('buttons.filter.btnText') }}
                            </button>
                        </div>
                        <div class="col-9">
                            <vs-checkbox v-model="onlyWithValidUsers"
                                         :name="'onlyWithValidUsers' + _uid"
                                         :id="'onlyWithValidUsers' + _uid"
                                         style="justify-content: flex-start !important">
                                {{ $t('registers.employees.onlyWithValidUsers') }}
                            </vs-checkbox>
                        </div>
                    </div>

                    <div class="input-group col-lg-6 col-md-12">
                        <input v-model="searchQuery" type="text"
                               v-on:keyup.enter.stop="massSearch"
                               v-on:keyup.esc.stop="clearFilters"
                               class="form-control"
                               aria-label="Mass search input"
                               aria-describedby="massSearch">
                        <button class="btn btn-secondary" @click.stop="massSearch">
                            <i class="fas fa-search"></i>
                            {{ $t('buttons.search.btnText') }}
                        </button>
                        <button class="btn btn-secondary"
                                @click.stop="clearFilters">
                            <i class="fas fa-times"></i>
                            {{ $t('buttons.clearFilters.btnText') }}
                        </button>
                    </div>
                </div>

                <kendo-datasource ref="datasource1"
                                  :transport="transportSettings"
                                  :schema-model-id="'id'"
                                  :schema-model-fields="schemaModelFields"
                                  :schema-total="'total'"
                                  :schema-data="'data'"
                                  :page-size='defaultGridRows'
                                  :server-paging="true"
                                  :server-sorting="true"
                                  :server-filtering="true"
                                  :error="dataSourceError">
                </kendo-datasource>

                <kendo-grid ref="grid"
                            :data-source-ref="'datasource1'"
                            :filterable="gridOptions.filterable"
                            :pageable="gridOptions.pageable"
                            :reorderable="gridOptions.reorderable"
                            :resizable="gridOptions.resizable"
                            :groupable="gridOptions.groupable"
                            :allow-copy="gridOptions.allowCopy"
                            :column-menu="gridOptions.columnMenu"
                            :sortable-mode="gridOptions.sortable.mode"
                            :sortable-allow-unsort="gridOptions.sortable.allowUnsort"
                            :sortable-show-indexes="gridOptions.sortable.showIndexes"
                            :selectable="gridOptions.selectable"
                            :scrollable="gridOptions.scrollable"
                            :toolbar="toolbar"
                            :excel="gridOptions.excel"
                            :dataBound="dataBound">
                    <kendo-grid-column field="id"
                                       :title="$t('registers.lectors.id')"/>
                    <kendo-grid-column field="fullName"
                                       :title="$t('person.firstName')"
                                       :hidden="isEnVersion" />
                    <kendo-grid-column field="fullNameEn"
                                       :title="$t('person.firstName')"
                                       :hidden="!isEnVersion" />
                    <kendo-grid-column field="administrativePositionName"
                                       :title="$t('registers.employees.gridHeaders.positionNij')"
                                       :hidden="isEnVersion" />
                    <kendo-grid-column field="administrativePositionNameEn"
                                       :title="$t('registers.employees.gridHeaders.positionNij')"
                                       :hidden="!isEnVersion" />
                    <kendo-grid-column field="directorateName"
                                       :title="$t('registers.employees.gridHeaders.directorate')"
                                       :hidden="isEnVersion" />
                    <kendo-grid-column field="directorateNameEn"
                                       :title="$t('registers.employees.gridHeaders.directorate')"
                                       :hidden="!isEnVersion" />
                    <kendo-grid-column field="workPlace"
                                       :title="$t('registers.employees.gridHeaders.workPlace')"
                                       :hidden="isEnVersion" />
                    <kendo-grid-column field="workPlaceEn"
                                       :title="$t('registers.employees.gridHeaders.workPlace')"
                                       :hidden="!isEnVersion" />
                    <kendo-grid-column field="email"
                                       :title="$t('person.gridHeaders.email')" />
                    <kendo-grid-column field="workPhone"
                                       :title="$t('registers.employees.gridHeaders.workPhone')" />
                    <kendo-grid-column field="isLeft"
                                       :title="$t('registers.employees.gridHeaders.isLeft')"
                                       :template="isLeftTemplate"
                                       hidden/>
                    <kendo-grid-column field="leavingDate"
                                       :title="$t('registers.employees.gridHeaders.leavingDate')"
                                       :format="customDateFormat"/>
                    <kendo-grid-column field="userName"
                                       :title="$t('registers.employees.gridHeaders.userName')"
                                       hidden/>
                    <kendo-grid-column field="isUserActive"
                                       :title="$t('registers.employees.gridHeaders.activeUser')"
                                       :template="isUserActiveTemplate"
                                       hidden />
                    <kendo-grid-column field="deleted"
                                       :title="$t('registers.employees.gridHeaders.deleted')"
                                       :template="isDeletedTemplate"/>
                    <kendo-grid-column :template="btnsTemplate"
                                       field="btnsColumn"
                                       :width="150"
                                       :filterable="false"
                                       :sortable="false"
                                       :title="$t('actions')"
                                       v-if="showBtns" />
                </kendo-grid>
            </div>
        </div>
    </div>
</template>

<script>
    import Vue from 'vue';
    import store from '@/store/store';
    import GridUserSettings from "@/components/grid/UserSettings";
    import BtnsTemplate from "@/components/grid/StandardBtnsTemplate";
    import PageTitle from '@/components/common/PageTitle';
    import personalizationService from '@/services/personalization.service';
    import JSZip from 'jszip';
    import { KendoGridPageSize } from '@/models/enums';
    import { mapGetters } from 'vuex';

    /*
    Renders the grid in "DejaVu Sans" font family, which is
    declared in kendo.common.css. It also declares the paths to the
    fonts below using <tt>kendo.pdf.defineFont</tt>, because the
    stylesheet is hosted on a different domain.
    */
    kendo.pdf.defineFont({
        "DejaVu Sans": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans.ttf",
        "DejaVu Sans|Bold": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Bold.ttf",
        "DejaVu Sans|Bold|Italic": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Oblique.ttf",
        "DejaVu Sans|Italic": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Oblique.ttf",
        "WebComponentsIcons": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/glyphs/WebComponentsIcons.ttf"
    });

    export default {
        name: 'EmployeesListView',
        props: {
            showHideButton: {
                default: false,
                type: Boolean
            },
            showPinButton: {
                default: true,
                type: Boolean
            },
            showNewButton: {
                default: true,
                type: Boolean
            },
            showExportTemplateButton: {
                default: false,
                type: Boolean
            },
            showImportFromExcelButton: {
                default: false,
                type: Boolean
            },
            showResizeIcon: {
                default: false,
                type: Boolean
            },
            size: {
                default: 6,
                type: Number
            },
            showBtns: {
                default: true,
                type: Boolean
            },
            showSettingsBtn: {
                default: true,
                type: Boolean
            }
        },
        components: {
            'grid-user-settings': GridUserSettings,
            PageTitle,
        },
        data() {
            return {
                componentName: "EmployeesRegisterGrid",
                titleKey: 'registers.employees.title',
                defaultGridRows: this.$store.getters.defaultGridRows,
                settingsTooltipText: this.$t('buttons.settings.tooltipText'),
                customSettingsLoaded: false,
                searchQuery: '',
                onlyWithValidUsers: false,
                schemaModelFields: {
                    id: {
                        name: 'id',
                        type: 'number',
                        editable: false,
                        nullable: true,
                        visible: true,
                    },
                    fullName: {
                        name: 'fullName',
                        type: 'string',
                        editable: false,
                        nullable: false,
                        visible: true,
                    },
                    fullNameEn: {
                        name: 'fullNameEn',
                        type: 'string',
                        editable: false,
                        nullable: false,
                        visible: true,
                    },
                    administrativePositionName: {
                        name: 'administrativePositionName',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                    },
                    administrativePositionNameEn: {
                        name: 'administrativePositionNameEn',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                    },
                    directorateName: {
                        name: 'directorateName',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                    },
                    directorateNameEn: {
                        name: 'directorateNameEn',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                    },
                    workPlace: {
                        name: 'workPlace',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                    },
                    workPlaceEn: {
                        name: 'workPlaceEn',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                    },
                    email: {
                        name: 'email',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                    },
                    workPhone: {
                        name: 'workPhone',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                    },
                    isLeft: {
                        name: 'isLeft',
                        type: "boolean",
                        editable: false,
                        nullable: true,
                        visible: true,
                    },
                    leavingDate: {
                        name: 'leavingDate',
                        type: "date",
                        editable: false,
                        nullable: true,
                        visible: true,
                    },
                    userName: {
                        name: 'userName',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                    },
                    isUserActive: {
                        name: 'isUserActive',
                        type: "boolean",
                        editable: false,
                        nullable: true,
                        visible: true,
                    },
                    deleted: {
                        name: 'deleted',
                        type: "boolean",
                        editable: false,
                        nullable: true,
                        visible: true,
                    },
                },
                rawHtml: "",
                transportSettings: {
                    read: {
                        url: `${store.getters.baseUrl}/api/employee/list`,
                        dataType: 'json',
                        type: 'POST',
                        contentType: 'application/json',
                        beforeSend: this.beforeSend
                    },
                    parameterMap: this.parameterMap
                },
                gridOptions: {
                    ...this.$store.getters.gridOptions,
                    excel: {
                        fileName: 'Employees.xlsx',
                        allPages: false,
                        filterable: true,
                        collapsible: true
                    },
                    pdf: {
                        fileName: 'Employees.pdf',
                        title: this.$t(this.titleKey),
                        subject: this.$t(this.titleKey),
                        creator: this.$store.userName,
                        allPages: false,
                        avoidLinks: true,
                        paperSize: 'A4',
                        margin: { top: '2cm', left: '1cm', right: '1cm', bottom: '1cm' },
                        landscape: true,
                        repeatHeaders: true,
                        scale: 0.6
                    }
                },
                showUserSettingsForm: false
            }
        },
        methods: {
            dataSourceError(e) {
                this.$vs.notify({
                    title: this.$t('notifications.error'),
                    text: this.$helpers.kendoDataSourceErrorText(e),
                    color: 'danger',
                    time: this.$store.getters.notificationDuration,
                });
            },
            goToCreate(event) {
                this.$router.push('/registers/employee/create');
            },
            goToEdit(args) {
                this.$router.push('/registers/employee/edit/' + args.id);
            },
            editInTewTabBtnClick(args) {
                let routeData = this.$router.resolve({ name: 'employeeEdit', params: { id: args.id } })
                window.open(routeData.href, '_blank')
            },
            goToDetails(args) {
                this.$router.push('/registers/employee/details/' + args.id);
            },
            detailsInTewTabBtnClick(args) {
                let routeData = this.$router.resolve({ name: 'employeeDetails', params: { id: args.id } })
                window.open(routeData.href, '_blank')
            },
            pdfExport(args) {
            },
            excelExport(args) {
            },
            goToExportTemplate() {
            },
            selectImportFiles() {
            },
            goToImportFromExcel(args) {
            },
            goToOrganogram() {
                if (!this.hasreadRights) {
                    return this.$router.push(`/unauthorized`);
                }

                let routeData = this.$router.resolve({ name: 'employeesOrganogram' });
                window.open(routeData.href, '_blank');
            },
            dataBound(e) {
                // This is needed for grid state save
                if (!this.customSettingsLoaded) {
                    this.loadGridSettings();
                }
            },
            parameterMap: function (options, operation) {
                if (operation !== 'read' && options.models) {
                    return { models: kendo.stringify(options.models) }
                } else {
                    this.saveState();
                    let newOptions = { onlyWithValidUsers: this.onlyWithValidUsers, request: options }
                    return kendo.stringify(newOptions);
                }
            },
            beforeSend: function (req) {
                if (this.token) {
                    req.setRequestHeader('Authorization', 'Bearer ' + this.token);
                }
            },
            btnsTemplate: function (e) {
                return {
                    template: Vue.component(BtnsTemplate.name, BtnsTemplate),
                    templateArgs: {
                        ...e,
                        parentComponent: this.$refs.grid,
                        btns: [{
                            name: "edit",
                            text: "",
                            tooltip: this.$t('buttons.edit.tooltipText'),
                            class: 'btn btn-success',
                            icon: "fas fa-edit",
                            show: this.claims.nijEmployee.update,
                            clickHandler: this.goToEdit
                        },
                        {
                            name: "editInNewTab",
                            text: this.$t('buttons.editInNewTab.btnText'),
                            tooltip: this.$t('buttons.editInNewTab.btnText'),
                            class: 'btn btn-primary',
                            icon: "fas fa-pencil-alt",
                            show: this.claims.nijEmployee.update,
                            clickHandler: this.editInTewTabBtnClick
                        },
                        {
                            name: "details",
                            text: "",
                            tooltip: this.$t('buttons.details.tooltipText'),
                            class: 'btn btn-info',
                            icon: "fas fa-eye",
                            show: this.claims.nijEmployee.read,
                            clickHandler: this.goToDetails
                        },
                        {
                            name: "detailsInNewTab",
                            text: "",
                            tooltip: this.$t('buttons.detailsInNewTab.btnText'),
                            class: 'btn btn-primary',
                            icon: "fas fa-eye",
                            show: this.claims.nijEmployee.read,
                            clickHandler: this.detailsInTewTabBtnClick
                        },
                        {
                            name: "delete",
                            text: "",
                            tooltip: this.$t('buttons.delete.tooltipText'),
                            class: 'btn btn-danger',
                            icon: "fas fa-times",
                            show: this.claims.nijEmployee.delete,
                            clickHandler: this.deleteBtnClick
                        },
                            //     {
                            //     name: "pdfExport",
                            //     text: "",
                            //     tooltip: this.$t('buttons.pdfExport.tooltipText'),
                            //     class: 'btn btn-danger',
                            //     icon: "far fa-file-pdf",
                            //     show: true,
                            //     clickHandler: this.pdfExport
                            // },
                            // {
                            //     name: "excelExport",
                            //     text: "",
                            //     tooltip: this.$t('buttons.excelExport.tooltipText'),
                            //     class: 'btn btn-success',
                            //     icon: "far fa-file-excel",
                            //     show: true,
                            //     clickHandler: this.excelExport
                            // },
                        ]
                    }
                };
            },
            // This is needed for grid state save and mass search
            getGridOptions() {
                const grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    return grid.getOptions();
                }
            },
            saveGridSettings() {
                const gridSettings = this.getGridOptions();
                const grid = this.$refs.grid.kendoWidget();
                if (gridSettings && grid) {
                    this.$refs.userSettings.setGridSettings(gridSettings);

                    personalizationService
                        .saveGridSettings(this.componentName, gridSettings)
                        .then(response => {
                            this.$vs.notify({
                                title: this.$t('request.response.status.' + this.$helpers.responseStatusCode(response)),
                                text: this.$helpers.responseMessage(response),
                                color: 'success',
                                time: this.$store.getters.notificationDuration
                            });
                        })
                        .catch(error => {
                            this.$vs.notify({
                                title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                                text: this.$helpers.errorText(error),
                                color: this.$helpers.errorColor(error),
                                time: this.$store.getters.notificationDuration
                            })
                        });
                }
            },
            loadGridSettings() {
                personalizationService
                    .loadGridSettings(this.componentName)
                    .then(response => {
                        try {
                            var options = personalizationService
                                .mergePersonGridSettings(this.getGridOptions(), response.data.settings);

                            let grid = this.$refs.grid.kendoWidget();
                            if (options && grid) {
                                grid.setOptions(options);

                                if (options.pageable.size !== grid.dataSource.options.pageSize) {
                                    grid.dataSource.pageSize(options.pageable.size);
                                    grid.refresh();
                                }

                                const userSettingsEditor = this.$refs.userSettings;
                                if (userSettingsEditor) {
                                    userSettingsEditor.setGridSettings(grid.getOptions());
                                }
                            }

                            this.customSettingsLoaded = true;
                        } catch (e) {
                            this.customSettingsLoaded = true;
                        }
                    })
                    .catch(error => {
                        this.customSettingsLoaded = true;

                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        })
                    });
            },
            deleteGridSettings() {
                personalizationService
                    .delteGridSettings(this.componentName)
                    .then(response => {
                        this.$router.go();
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        })
                    });
            },
            userSettingsChanged(userSettings, pageSizeChanged) {
                let grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    try {
                        grid.setOptions(userSettings);
                        if (pageSizeChanged === true) {
                            if (userSettings.pageable.size === KendoGridPageSize.All) {
                                grid.dataSource.pageSize(this.$refs["datasource1"].kendoDataSource.total());
                            } else {
                                grid.dataSource.pageSize(userSettings.pageable.size);
                            }
                            grid.refresh();
                        }
                    } catch (e) {
                    }
                }
            },
            saveState() {
                personalizationService.saveState(this.componentName, this.getGridOptions());
            },
            loadState: function () {
                let stateSettings = personalizationService.getState(this.componentName);
                let options = personalizationService.mergeStateSettings(this.getGridOptions(), stateSettings)
                if (options) {
                    let grid = this.$refs.grid.kendoWidget();
                    if (grid) {
                        grid.setOptions(options);
                    }
                }
            },
            changeGridSettings() {
                this.showUserSettingsForm = !this.showUserSettingsForm;
            },
            clearFilters() {
                personalizationService.claerState(this.componentName);

                this.searchQuery = '';
                this.onlyWithValidUsers = false;
                const grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    grid.dataSource.sort({});
                    grid.dataSource.page(1);
                    grid.dataSource.aggregate([]);
                    grid.dataSource.filter({});
                }
            },
            massSearch() {
                if (!this.searchQuery) {
                    this.$vs.notify({
                        title: this.$t('buttons.search.btnText'),
                        text: this.$t('emptySearchFilter'),
                        color: 'warning',
                        time: this.$store.getters.notificationDuration
                    })

                    return;
                }

                let grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    let filter = this.$helpers.getMassFilterForGrid(grid, this.searchQuery);
                    if (filter) {
                        grid.dataSource.filter(filter);
                    }
                }
            },
            refresh() {
                const grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    grid.dataSource.read()
                }
            },
            deleteBtnClick(args) {
                kendo.confirm(`${this.$t('buttons.delete.confirmationText1', { name: args.name })}`)
                    .then(function () {
                        this.deleteEmployee(args.id);
                    }.bind(this));
            },
            deleteEmployee(id) {
                this.$http.delete('/api/employee/',
                    { params: { id: id } }
                ).then((response) => {
                    this.refresh();
                    let statusCode = Number(this.$helpers.responseStatusCode(response));
                    this.$vs.notify({
                        title: this.$t('buttons.delete.action'),
                        text: this.$helpers.responseMessage(response),
                        color: statusCode === 240 ? 'warning' : 'success',
                        time: this.$store.getters.notificationDuration,
                    })
                }).catch(error => {
                    this.$vs.notify({
                        title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                        text: this.$helpers.errorText(error),
                        color: 'danger',
                        time: this.$store.getters.notificationDuration
                    })
                })
            },
            isUserActiveTemplate(dataItem) {
                let templateHtml =
                    '<span class="badge ' +
                    (dataItem.isUserActive === true ? "badge-success" : "badge-danger") +
                    '">' +
                    kendo.htmlEncode(
                        dataItem.isUserActive === true ? this.$t("yes") : this.$t("no")
                    ) +
                    "</span>";

                return templateHtml;
            },
            isLeftTemplate(dataItem) {
                let templateHtml =
                    '<span class="badge ' +
                    (dataItem.isLeft === true ? "badge-warning" : "badge-light") +
                    '">' +
                    kendo.htmlEncode(
                        dataItem.isLeft === true ? this.$t("yes") : this.$t("no")
                    ) +
                    "</span>";

                return templateHtml;
            },
            isDeletedTemplate(dataItem) {
                let templateHtml =
                    '<span class="badge ' +
                    (dataItem.deleted === true ? "badge-danger" : "badge-success") +
                    '">' +
                    kendo.htmlEncode(
                        dataItem.deleted === true ? this.$t("yes") : this.$t("no")
                    ) +
                    "</span>";

                return templateHtml;
            },
        },
        mounted: function () {
            var container = $(this.$el);
            container.find('[data-toggle="tooltip"]').tooltip({ container });
        },
        computed: {
            ...mapGetters(['claims', 'isEnVersion', 'dateFormat']),
            token: function () {
                return store.getters.authToken;
            },
            userSettings() {
                let grid = this.$refs.grid.kendoWidget();
                return grid.getOptions();
            },
            toolbar: function () {
                return [{ name: 'excel', text: this.$t('buttons.excelExport.tooltipText') }]
            },
            hasreadRights() {
                return this.$store.getters.claims.nijEmployee.read;
            },
            customDateFormat() {
                return `{0:${this.dateFormat}}`;
            },
        },
        created: function () {
            window.JSZip = JSZip // Необходимо е за да работи експорта в excel
        },
        watch: {
            customSettingsLoaded: function (val) {
                if (val === true) {
                    // this.loadState();
                }
            }
        }
    }
</script>

<style scoped>
    .btn {
        margin-right: 10px;
        margin-left: 5px;
    }

    .checkbox {
        margin-right: 5px;
    }

    .selectFile {
        display: none;
    }

    .inlineForm {
        display: inline;
    }
</style>
